"use client";
import useTheme from "@/hooks/ui/useTheme";
import {
    IAchievement,
    ICaseStudy,
    ICertificate,
    IEducation,
    IExperience,
    IProject,
    ISkillCategory,
    IStats,
    ITestimonial,
    IUser,
} from "@/types";
import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from "react";

interface AppContextType {
    user: IUser | null;
    setUser: React.Dispatch<React.SetStateAction<IUser | null>>;
    experiences: IExperience[];
    setExperiences: React.Dispatch<React.SetStateAction<IExperience[]>>;
    educations: IEducation[];
    setEducations: React.Dispatch<React.SetStateAction<IEducation[]>>;
    skills: ISkillCategory[];
    setSkills: React.Dispatch<React.SetStateAction<ISkillCategory[]>>;
    projects: IProject[];
    setProjects: React.Dispatch<React.SetStateAction<IProject[]>>;
    caseStudies: ICaseStudy[];
    setCaseStudies: React.Dispatch<React.SetStateAction<ICaseStudy[]>>;
    achievements: IAchievement[];
    setAchievements: React.Dispatch<React.SetStateAction<IAchievement[]>>;
    certificates: ICertificate[];
    setCertificates: React.Dispatch<React.SetStateAction<ICertificate[]>>;
    testimonials: ITestimonial[];
    setTestimonials: React.Dispatch<React.SetStateAction<ITestimonial[]>>;
    stats: IStats | null;
    setStats: React.Dispatch<React.SetStateAction<IStats | null>>;
    theme: "light" | "dark";
    toggleTheme: () => void;
    loading: boolean;
    showBanner: boolean;
    setShowBanner: React.Dispatch<React.SetStateAction<boolean>>;
    mode: "tech" | "simple";
    toggleMode: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("useAppContext must be used within a AppProvider");
    }
    return context;
};

interface AppProviderProps {
    children: ReactNode;
    initialData?: {
        user: IUser;
        educations: IEducation[];
        experiences: IExperience[];
        skills: ISkillCategory[];
        projects: IProject[];
        achievements: IAchievement[];
        certificates: ICertificate[];
        testimonials: ITestimonial[];
        stats: IStats;
    };
}

export const AppProvider = ({ children, initialData }: AppProviderProps) => {
    const [user, setUser] = useState<IUser | null>(initialData?.user ?? null);
    const [educations, setEducations] = useState<IEducation[]>(
        initialData?.educations ?? [],
    );
    const [experiences, setExperiences] = useState<IExperience[]>(
        initialData?.experiences ?? [],
    );
    const [skills, setSkills] = useState<ISkillCategory[]>(
        initialData?.skills ?? [],
    );
    const [projects, setProjects] = useState<IProject[]>(
        initialData?.projects ?? [],
    );
    const [caseStudies, setCaseStudies] = useState<ICaseStudy[]>([]);
    const [achievements, setAchievements] = useState<IAchievement[]>(
        initialData?.achievements ?? [],
    );
    const [certificates, setCertificates] = useState<ICertificate[]>(
        initialData?.certificates ?? [],
    );
    const [testimonials, setTestimonials] = useState<ITestimonial[]>(
        initialData?.testimonials ?? [],
    );
    const [stats, setStats] = useState<IStats | null>(
        initialData?.stats ?? null,
    );

    const { theme, toggleTheme } = useTheme();
    const [loading, setLoading] = useState(!initialData); // If no server data, show loading

    const [showBanner, setShowBanner] = useState(false);
    const [mode, setMode] = useState<"tech" | "simple">("tech");

    // Restore selected primary accent color from localStorage on initial load on any route
    useEffect(() => {
        if (typeof window !== "undefined") {
            const storedColor = localStorage.getItem("primary-color");
            if (storedColor) {
                document.documentElement.style.setProperty(
                    "--primary-color",
                    storedColor,
                );
            }
        }
    }, []);

    useEffect(() => {
        if (!initialData) {
            const fetchPortfolio = async () => {
                try {
                    const response = await fetch("/api/portfolio", {
                        cache: "no-cache",
                    });
                    const data = await response.json();
                    setUser(data.data.user);
                    setEducations(data.data.educations);
                    setExperiences(data.data.experiences);
                    setSkills(data.data.skills);
                    setProjects(data.data.projects);
                    setAchievements(data.data.achievements);
                    setCertificates(data.data.certificates);
                    setTestimonials(data.data.testimonials);
                    setStats(data.data.stats);
                } catch {
                    throw new Error("Failed to fetch portfolio");
                } finally {
                    setLoading(false);
                }
            };

            fetchPortfolio();
        }
    }, [initialData]);

    const toggleMode = () => {
        setMode((prev) => (prev === "tech" ? "simple" : "tech"));
    };

    return (
        <AppContext.Provider
            value={{
                user,
                setUser,
                experiences,
                setExperiences,
                educations,
                setEducations,
                skills,
                setSkills,
                projects,
                setProjects,
                caseStudies,
                setCaseStudies,
                achievements,
                setAchievements,
                certificates,
                setCertificates,
                testimonials,
                setTestimonials,
                stats,
                setStats,
                theme,
                toggleTheme,
                loading,
                showBanner,
                setShowBanner,
                mode,
                toggleMode,
            }}
        >
            {children}
        </AppContext.Provider>
    );
};
